import useAuthStore from '../stores/authStore';
import authService from '../services/authService';

// ``useAuth`` is a PURE state-access hook — no side effects.
//
// The verify-connection + timezone-sync side effects USED to live here, but
// 28 components in the app mount this hook, so each auth state change fanned
// out into ~7 parallel ``/third-party/verify-connection`` calls and ~7
// ``PATCH /users/me/timezone`` writes. The side effects moved to
// ``useAuthBootstrap``, which is mounted exactly once in ``AppLayout``.
// Components that need to TRIGGER a verify manually use
// ``authService.checkEtsyConnection()`` directly — it has its own
// module-level dedup.
// Stable ONCE, at module scope. These forward to `authService` at CALL time
// rather than binding at module-eval time, because `authService` imports
// `subscriptionStore`, which imports `useApi`, which imports this file — so at
// the moment this module is evaluated `authService`'s methods may not exist yet
// and `.bind()` would throw. Calling `authService.x(...)` also keeps `this`
// bound to the service, exactly as `.bind(authService)` did.
//
// The point is the IDENTITY. `.bind()` allocated a new function on every render,
// so every consumer got a fresh `logout`/`login`/... each time. Nothing
// downstream could be memoised: `useApi` depends on `logout`, so its object
// churned too, and with it every `useCallback` in the Shopify hooks and pages.
const BOUND = {
  login: (...a) => authService.loginUser(...a),
  register: (...a) => authService.registerUser(...a),
  logout: (...a) => authService.logoutUser(...a),
  refreshUserData: (...a) => authService.refreshUserData(...a),
  handleEtsyOAuth: (...a) => authService.handleEtsyOAuthCallback(...a),
  disconnectEtsy: (...a) => authService.disconnectEtsy(...a),
  checkEtsyConnection: (...a) => authService.checkEtsyConnection(...a),
  makeAuthenticatedRequest: (...a) => authService.makeAuthenticatedRequest(...a),
};

export const useAuth = () => {
  const {
    // User state
    user,
    userToken,
    isUserAuthenticated,
    userLoading,
    userError,

    // Etsy state
    etsyUserInfo,
    etsyShopInfo,
    isEtsyConnected,
    etsyLoading,
    etsyError,

    // Server-authoritative connection state (#369). isEtsyConnected is a
    // boolean and cannot express "revoked, reconnect required" — this can.
    etsyConnectionState,
    etsyReconnectSince,
    etsyReconnectDetail,
    etsyReconnectShops,

    // Rate-limit state (backend signals these when Etsy returns 429)
    etsyRateLimited,
    etsyStale,
    etsyRetryAfter,

    // Helper methods
    isEtsyTokenValid,
    getValidEtsyToken,
    getDebugInfo,
    updateUser,
  } = useAuthStore();

  return {
    // User authentication
    user,
    userToken,
    isUserAuthenticated,
    userLoading,
    userError,
    login: BOUND.login,
    register: BOUND.register,
    logout: BOUND.logout,
    refreshUserData: BOUND.refreshUserData,
    updateUser,

    // Etsy authentication
    etsyUserInfo,
    etsyShopInfo,
    isEtsyConnected,
    etsyLoading,
    etsyError,
    etsyConnectionState,
    etsyReconnectSince,
    etsyReconnectDetail,
    etsyReconnectShops,
    etsyRateLimited,
    etsyStale,
    etsyRetryAfter,
    isEtsyTokenValid: isEtsyTokenValid(),
    getValidEtsyToken,
    handleEtsyOAuth: BOUND.handleEtsyOAuth,
    disconnectEtsy: BOUND.disconnectEtsy,
    checkEtsyConnection: BOUND.checkEtsyConnection,

    // API helpers
    makeAuthenticatedRequest: BOUND.makeAuthenticatedRequest,

    // Debug
    getDebugInfo,
  };
};

export default useAuth;
